/**
 * PotBanner — floating holographic pot readout above the cauldron.
 *
 * Replaces the old crimson-velvet cloth banner with a projected hologram: a
 * faint translucent cyan panel, glowing text (POT / amount / NEXT TOUCH / fee),
 * and a spinning hologram medallion. The whole projection bobs and flickers
 * gently. When `round.potLamports` increases — same delta trigger the cauldron
 * uses for burst coins — the medallion flares brighter, keeping every touch a
 * synchronized cue (cauldron bursts + hologram flare).
 *
 * Layout in component frame (origin = the float point in mid-air; +Y = up):
 *   medallion ring at the top, then stacked text below it, panel behind.
 * The anchor is positioned at a readable mid-air height in Lobby.tsx — there's
 * no physical rig, so it just hangs in the air over the pot.
 */
import { Text } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import * as THREE from "three";

import type { KothRoundState } from "./useKothRound";

const KOTH_BASE_FEE_LAMPORTS = 1_000_000n;
const KOTH_STEP_FEE_LAMPORTS = 100_000n;
const LAMPORTS_PER_SOL = 1_000_000_000n;

const PULSE_DURATION = 0.6;
const PANEL_W = 2.0;
const PANEL_H = 2.2;

// Hologram palette.
const HOLO = "#5fd8ff";
const HOLO_BRIGHT = "#eafcff";
const HOLO_DIM = "#6fbfd6";
const HOLO_DEEP = "#0a3a4a";

interface PotBannerProps {
  round: KothRoundState | null;
  /** World position of the mid-air float point for the hologram. */
  position: [number, number, number];
}

export function PotBanner({ round, position }: PotBannerProps): JSX.Element {
  const potSol = round ? lamportsToSol(toBig(round.potLamports)) : "0.0000";
  const touchCount = round?.touchCount ?? 0;
  const nextFeeSol = lamportsToSol(nextFeeLamports(touchCount));

  const groupRef = useRef<THREE.Group>(null);
  const ringRef = useRef<THREE.Group>(null);
  const ringMatRef = useRef<THREE.MeshBasicMaterial>(null);
  const discMatRef = useRef<THREE.MeshBasicMaterial>(null);
  const panelMatRef = useRef<THREE.MeshBasicMaterial>(null);
  const pulseRef = useRef(0);
  const prevLamportsRef = useRef<bigint | null>(null);

  const lamportsKey = round ? round.potLamports.toString() : "none";
  useEffect(() => {
    const lamports = round ? toBig(round.potLamports) : 0n;
    const prev = prevLamportsRef.current;
    if (prev !== null && lamports > prev) {
      pulseRef.current = PULSE_DURATION;
    }
    prevLamportsRef.current = lamports;
  }, [lamportsKey, round]);

  useFrame((state, dt) => {
    const t = state.clock.elapsedTime;

    // Gentle mid-air float + holographic shimmer.
    if (groupRef.current) {
      groupRef.current.position.y = position[1] + Math.sin(t * 0.9) * 0.06;
      groupRef.current.rotation.y = Math.sin(t * 0.5) * 0.025;
      groupRef.current.rotation.x = Math.sin(t * 0.7) * 0.02;
    }

    // Spinning medallion.
    if (ringRef.current) {
      ringRef.current.rotation.y += dt * 0.8;
    }

    // Subtle projection flicker on the backing panel.
    if (panelMatRef.current) {
      const flicker =
        0.07 + 0.05 * Math.abs(Math.sin(t * 9.0)) * (0.6 + 0.4 * Math.sin(t * 23.0));
      panelMatRef.current.opacity = flicker;
    }

    // Pot-increase flare on the medallion.
    if (pulseRef.current > 0) {
      pulseRef.current = Math.max(0, pulseRef.current - dt);
    }
    const p = pulseRef.current / PULSE_DURATION;
    if (ringMatRef.current) ringMatRef.current.opacity = 0.55 + 0.45 * p;
    if (discMatRef.current) discMatRef.current.opacity = 0.12 + 0.35 * p;
  });

  return (
    <group ref={groupRef} position={position}>
      {/* Translucent projection panel — the "transparent banner". Additive so
          it reads as light, not cloth; depthWrite off so text never z-fights. */}
      <mesh position={[0, 0.1, -0.05]}>
        <planeGeometry args={[PANEL_W, PANEL_H]} />
        <meshBasicMaterial
          ref={panelMatRef}
          color={HOLO}
          transparent
          opacity={0.08}
          blending={THREE.AdditiveBlending}
          side={THREE.DoubleSide}
          depthWrite={false}
        />
      </mesh>

      {/* Spinning hologram medallion (ring + faint inner disc) — flares on a
          pot increase, the same cue as the cauldron coin burst. */}
      <group ref={ringRef} position={[0, 0.95, 0]}>
        <mesh>
          <torusGeometry args={[0.32, 0.03, 16, 48]} />
          <meshBasicMaterial
            ref={ringMatRef}
            color={HOLO_BRIGHT}
            transparent
            opacity={0.55}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
        <mesh>
          <circleGeometry args={[0.3, 48]} />
          <meshBasicMaterial
            ref={discMatRef}
            color={HOLO}
            transparent
            opacity={0.12}
            blending={THREE.AdditiveBlending}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
      </group>

      {/* POT title */}
      <HoloText y={0.45} size={0.26} color={HOLO} letter={0.1}>
        POT
      </HoloText>
      {/* Pot amount — the hero line */}
      <HoloText y={0.1} size={0.3} color={HOLO_BRIGHT}>
        {`${potSol} SOL`}
      </HoloText>
      {/* Net-payable subtext */}
      <HoloText y={-0.13} size={0.1} color={HOLO_DIM} letter={0.06}>
        net payable
      </HoloText>
      {/* NEXT TOUCH label */}
      <HoloText y={-0.46} size={0.16} color={HOLO} letter={0.06}>
        NEXT TOUCH
      </HoloText>
      {/* Next-touch fee */}
      <HoloText y={-0.74} size={0.22} color={HOLO_BRIGHT}>
        {`${nextFeeSol} SOL`}
      </HoloText>
    </group>
  );
}

/** Glowing holographic text line (additive halo via blurred outline). */
function HoloText({
  y,
  size,
  color,
  letter = 0,
  children,
}: {
  y: number;
  size: number;
  color: string;
  letter?: number;
  children: string;
}): JSX.Element {
  return (
    <Text
      position={[0, y, 0.02]}
      fontSize={size}
      color={color}
      anchorX="center"
      anchorY="middle"
      letterSpacing={letter}
      fillOpacity={0.92}
      outlineWidth={size * 0.06}
      outlineColor={HOLO}
      outlineBlur={size * 0.9}
      outlineOpacity={0.5}
      strokeColor={HOLO_DEEP}
      strokeWidth={size * 0.015}
    >
      {children}
    </Text>
  );
}

function toBig(bn: { toString(): string }): bigint {
  return BigInt(bn.toString());
}

function nextFeeLamports(touchCount: number): bigint {
  return KOTH_BASE_FEE_LAMPORTS + KOTH_STEP_FEE_LAMPORTS * BigInt(touchCount);
}

function lamportsToSol(lamports: bigint): string {
  const whole = lamports / LAMPORTS_PER_SOL;
  const frac = lamports % LAMPORTS_PER_SOL;
  return `${whole}.${frac.toString().padStart(9, "0").slice(0, 4)}`;
}
